import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import type { Map as LeafletMap, Layer } from 'leaflet';
import { Loader } from 'google-maps';
import TCCCredentials from '@tcc/credentials';
import env from '@tcc/env-detect';
import { Map } from '@tcc/treadstone';
import { logDmbError } from '../../../utils/error';
import { equalControlledViewports } from '../../../utils/viewport';

type MapReadyEvent = {
  target: LeafletMap;
};

type SafeTCCMapProps = Omit<ComponentProps<typeof Map>, 'ref' | 'whenReady'> & {
  attributionControl?: boolean;
  children?: ReactNode;
  className?: string;
  maxZoom?: number;
  minZoom?: number;
  type?: string;
  whenReady?: (event: MapReadyEvent) => void;
  zoomControl?: boolean;
};

export type LegacyMapRef = {
  leafletElement: LeafletMap;
};

type LeafletGlobal = typeof globalThis & {
  L?: {
    control: {
      zoom: (options: { position: string }) => {
        addTo: (map: LeafletMap) => unknown;
        remove: () => void;
      };
    };
    gridLayer: {
      googleMutant: (options: {
        pane: 'tilePane';
        type: string;
        styles: string[];
      }) => Layer;
    };
  };
};

let googleMapsLoadPromise: Promise<unknown> | undefined;

export const resetGoogleMapsLoadPromise = () => {
  googleMapsLoadPromise = undefined;
};

const getGoogleMapsLoadPromise = () => {
  if (!googleMapsLoadPromise) {
    const currentEnv = env.getCurrentEnv();
    const googleConfig = TCCCredentials.forEnv(currentEnv).google;
    googleMapsLoadPromise = new Loader(googleConfig.key, {
      version: 'weekly',
    }).load();
  }

  return googleMapsLoadPromise;
};

export const isLeafletMapReadyForGoogleLayer = (map: LeafletMap) =>
  map.getContainer().isConnected &&
  Boolean(map.getPane('tilePane')?.isConnected);

export const SafeTCCMap = forwardRef<LegacyMapRef, SafeTCCMapProps>(({
  attributionControl = false,
  maxZoom = 18,
  minZoom = 0,
  type = 'hybrid',
  zoomControl = false,
  children,
  className,
  onViewportChanged,
  viewport,
  whenReady,
  ...rest
}, ref) => {
  const [map, setMap] = useState<LeafletMap | null>(null);
  const isUserDraggingRef = useRef(false);
  const isUserZoomingRef = useRef(false);
  const handleReady = useCallback((event: MapReadyEvent) => {
    setMap(event.target);
    whenReady?.(event);
  }, [whenReady]);

  useEffect(() => {
    const leaflet = (globalThis as LeafletGlobal).L;

    if (!map || !leaflet || !isLeafletMapReadyForGoogleLayer(map)) {
      return undefined;
    }

    map.setMaxZoom(maxZoom);
    map.setMinZoom(minZoom);

    if (!attributionControl && map.attributionControl) {
      map.attributionControl.remove();
    }

    if (map.zoomControl) {
      map.zoomControl.remove();
    }

    // Preserve TCCMap's legacy contract: false replaces the default control
    // with one at the bottom right, while true leaves zoom controls hidden.
    const customZoomControl = zoomControl
      ? undefined
      : leaflet.control.zoom({ position: 'bottomright' });
    customZoomControl?.addTo(map);

    return () => {
      customZoomControl?.remove();
    };
  }, [attributionControl, map, maxZoom, minZoom, zoomControl]);

  useEffect(() => {
    const leaflet = (globalThis as LeafletGlobal).L;

    if (!map || !leaflet || !isLeafletMapReadyForGoogleLayer(map)) {
      return undefined;
    }

    let isDisposed = false;
    let googleLayer: Layer | undefined;
    const handleMapUnload = () => {
      isDisposed = true;
      googleLayer = undefined;
    };

    map.once('unload', handleMapUnload);

    const loadPromise = getGoogleMapsLoadPromise();
    loadPromise
      .then(() => {
        if (isDisposed || !isLeafletMapReadyForGoogleLayer(map)) {
          return;
        }

        googleLayer = leaflet.gridLayer.googleMutant({
          pane: 'tilePane',
          type,
          styles: [],
        });
        googleLayer.addTo(map);
      })
      .catch((error: unknown) => {
        if (googleMapsLoadPromise === loadPromise) {
          resetGoogleMapsLoadPromise();
        }

        const loadError = error instanceof Error
          ? error
          : new Error('Google Maps failed to load');
        logDmbError(loadError, {
          appLocation: 'SafeTCCMap',
          operation: 'loadGoogleMaps',
        });
      });

    return () => {
      isDisposed = true;
      map.off('unload', handleMapUnload);
      if (googleLayer && map.hasLayer(googleLayer)) {
        map.removeLayer(googleLayer);
      }
    };
  }, [map, type]);

  const controlledViewport = (() => {
    if (
      !map ||
      !viewport?.center ||
      isUserDraggingRef.current ||
      isUserZoomingRef.current
    ) {
      return undefined;
    }

    const center = Array.isArray(viewport.center)
      ? viewport.center
      : [viewport.center.lat, viewport.center.lng];
    const mapCenter = map.getCenter();
    const targetViewport = {
      center,
      zoom: Number.isFinite(viewport.zoom) ? viewport.zoom : map.getZoom(),
    };
    const mapViewport = {
      center: [mapCenter.lat, mapCenter.lng],
      zoom: map.getZoom(),
    };

    return equalControlledViewports(mapViewport, targetViewport)
      ? undefined
      : viewport;
  })();

  useEffect(() => {
    if (!map || !onViewportChanged) {
      return undefined;
    }

    const publishViewport = () => {
      const mapCenter = map.getCenter();
      onViewportChanged({
        center: {
          lat: mapCenter.lat,
          lng: mapCenter.lng,
        },
        zoom: map.getZoom(),
      });
    };
    const handleDragStart = () => {
      isUserDraggingRef.current = true;
    };
    const handleMoveEnd = () => {
      if (isUserDraggingRef.current) {
        isUserDraggingRef.current = false;
        publishViewport();
      }

    };
    const handleZoomStart = () => {
      isUserZoomingRef.current = true;
    };
    const handleZoomEnd = () => {
      isUserZoomingRef.current = false;
      publishViewport();
    };

    map.on('dragstart', handleDragStart);
    map.on('moveend', handleMoveEnd);
    map.on('zoomstart', handleZoomStart);
    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('dragstart', handleDragStart);
      map.off('moveend', handleMoveEnd);
      map.off('zoomstart', handleZoomStart);
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onViewportChanged]);

  return (
    <Map
      {...rest}
      attributionControl={attributionControl}
      className={['tcc-map', className].filter(Boolean).join(' ')}
      maxZoom={maxZoom}
      minZoom={minZoom}
      ref={ref}
      viewport={controlledViewport}
      whenReady={handleReady}
      zoomControl={false}
    >
      {children}
    </Map>
  );
});

SafeTCCMap.displayName = 'SafeTCCMap';
